import { AppstoreOutlined, PlusOutlined } from "@ant-design/icons";
import type { UserOrganization } from "@m-smart/shared";
import { Button, Skeleton, Space, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMe } from "@/modules/auth/hooks/useAuth";
import { EmptyState } from "@/shared/components/EmptyState";
import { PageHeader } from "@/shared/components/PageHeader";
import { StatusTag } from "@/shared/components/StatusTag";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { FaceEnrollmentModal } from "../components/FaceEnrollmentModal";
import { FaceEnrollmentTable } from "../components/FaceEnrollmentTable";
import { useFaceEnrollmentStore } from "../store/face-enrollment.store";

export function FaceEnrollmentPage() {
	const { page, limit, setPage, getFilter, setFilters } = useUrlTableState();
	const selectedOrgId = getFilter("org") ?? null;

	const { data: meUser, isLoading: isMeLoading } = useMe();
	const openAddModal = useFaceEnrollmentStore((s) => s.openAddModal);

	const organizations = meUser?.organizations ?? [];
	const selectedOrg = selectedOrgId ? organizations.find((o) => o.id === selectedOrgId) : null;

	const handleSelectOrg = (id: string) => {
		// Org drill-down + page reset in a single history entry.
		setFilters({ org: id });
	};

	return (
		<div>
			<PageHeader
				title="Yuz ro'yxatga olish"
				action={
					selectedOrgId ? (
						<Space>
							<Button
								type="default"
								icon={<AppstoreOutlined />}
								onClick={() => setFilters({ org: null })}
							>
								Tashkilotlarni ko'rish
							</Button>
							<Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
								Qo'shish
							</Button>
						</Space>
					) : undefined
				}
			/>

			{isMeLoading ? (
				<Skeleton active paragraph={{ rows: 5 }} />
			) : organizations.length === 0 ? (
				<EmptyState description="Tashkilot topilmadi — yuzlarni ro'yxatga olish uchun tashkilotga qo'shilishingiz kerak" />
			) : selectedOrgId ? (
				<>
					<FaceEnrollmentTable
						organizationId={selectedOrgId}
						organizationName={selectedOrg?.name ?? undefined}
						page={page}
						limit={limit}
						onPageChange={setPage}
					/>
					<FaceEnrollmentModal organizationId={selectedOrgId} />
				</>
			) : (
				<Table
					dataSource={organizations}
					rowKey="id"
					loading={false}
					onRow={(org) => ({
						onClick: () => handleSelectOrg(org.id),
						className: "cursor-pointer",
					})}
					columns={
						[
							{
								title: "№",
								key: "index",
								width: 60,
								align: "center",
								render: (_1: unknown, _2: unknown, index: number) => index + 1,
							},
							{
								title: "Nomi",
								dataIndex: "name",
								key: "name",
								ellipsis: true,
								render: (name: string) => <span className="font-medium">{name}</span>,
							},
							{
								title: "Holat",
								key: "isPrimary",
								width: 100,
								render: (_: unknown, org: UserOrganization) =>
									org.isPrimary ? <StatusTag tone="processing">Asosiy</StatusTag> : null,
							},
						] as ColumnsType<UserOrganization>
					}
					pagination={false}
					locale={{ emptyText: <EmptyState description="Tashkilotlar yo'q" /> }}
				/>
			)}
		</div>
	);
}
